/**
 * Customer "Advanced report" (spec §2, track T4): every finding of the run, customisable and actionable.
 * Presentational over a ReportStore; the route wrapper (AdvancedReportPage) wires the BFF client, flag and URL.
 * States: flag off, loading, not found (report not stored yet), error (retry), ready (also with zero findings).
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, EmptyState, ErrorState, Loading, PageHeader } from '@qm/ui';
import type { AdvancedReport } from '@qm/contracts/audit-remediation';
import { canWriteFindings, toCsv, visibleFindings, type Section } from './model';
import { CompetitorsSection, CoverageSection, SignalsSection, SummarySection } from './ExecutiveHeader';
import { FindingDrawer } from './FindingDrawer';
import { FindingsSection } from './FindingsSection';
import { RoadmapSection } from './RoadmapSection';
import { HeaderSlot, ReportSlotsProvider, DEFAULT_SLOTS, type ReportSlotComponents } from './slots';
import { useReportState, type ReportStore, type ReportUiState } from './store';
import { ReportTextProvider, useReportText } from './text';
import { ViewsBar } from './ViewsBar';
import './report.css';

export interface AdvancedReportViewProps {
  store: ReportStore;
  roles: readonly string[] | undefined;
  /** 'off' renders the flag-off state without calling the API. */
  flag?: 'on' | 'off' | 'loading';
  /** ISO "now" for the progress chart (injected for tests). */
  now?: string;
  timeZone?: string;
  /** Replace the T5 / T10 placeholders (integration point, see slots.tsx). */
  slots?: Partial<ReportSlotComponents>;
  /** Called when the open finding changes (the page mirrors it into ?finding=). */
  onSelectionChange?: (id: string | null) => void;
  /** Triggers the browser file save (injected in tests). */
  download?: (filename: string, content: string) => void;
  /** window.print (injected in tests). */
  print?: () => void;
}

export function AdvancedReportView(p: AdvancedReportViewProps) {
  const state = useReportState(p.store);
  return (
    <ReportTextProvider loc={state.contentLocale} timeZone={p.timeZone}>
      <ReportSlotsProvider value={{ ...DEFAULT_SLOTS, ...p.slots }}>
        <Body {...p} state={state} />
      </ReportSlotsProvider>
    </ReportTextProvider>
  );
}

function Frame({ children, actions, sub }: { children: ReactNode; actions?: ReactNode; sub?: string }) {
  const { t, loc, dir } = useReportText();
  return (
    <section className="qm-page qm-ar" aria-labelledby="page-title" lang={loc} dir={dir}>
      <PageHeader title={t('audit-remediation.report.title')} eyebrow={sub ?? t('audit-remediation.report.eyebrow')} intro={t('audit-remediation.report.intro')} {...(actions ? { actions } : {})} />
      {children}
    </section>
  );
}

function Body({ store, state, roles, flag = 'on', now, onSelectionChange, download = saveFile, print = printPage }: AdvancedReportViewProps & { state: ReportUiState }) {
  const { t } = useReportText();
  const back = <Link className="qm-text-link" to={`/audit/${encodeURIComponent(store.jobId)}`}>{t('audit-remediation.report.back')}</Link>;

  useEffect(() => {
    if (!state.printMode) return;
    print();
    store.setPrintMode(false);
  }, [state.printMode, print, store]);

  if (flag === 'loading') return <Frame actions={back}><Loading label={t('audit-remediation.report.loading')} /></Frame>;
  const r = state.report;
  if (flag === 'off' || (r.status === 'error' && r.kind === 'flag_off')) {
    return <Frame actions={back}><EmptyState title={t('audit-remediation.report.flagOff.title')}><p>{t('audit-remediation.report.flagOff.body')}</p>{back}</EmptyState></Frame>;
  }
  if (r.status === 'loading') return <Frame actions={back}><Loading label={t('audit-remediation.report.loading')} /></Frame>;
  if (r.status === 'error') {
    return (
      <Frame actions={back}>
        {r.kind === 'not_found'
          ? <EmptyState title={t('audit-remediation.report.notReady.title')}><p>{t('audit-remediation.report.notReady.body')}</p>{back}</EmptyState>
          : <><ErrorState message={t('audit-remediation.report.errors.load')} /><Button onClick={() => void store.load()}>{t('audit-remediation.report.retry')}</Button></>}
      </Frame>
    );
  }
  return <Ready report={r.data} state={state} store={store} roles={roles} now={now ?? new Date().toISOString()} back={back}
    onSelectionChange={onSelectionChange} download={download} />;
}

function Ready({ report, state, store, roles, now, back, onSelectionChange, download }: {
  report: AdvancedReport; state: ReportUiState; store: ReportStore; roles: readonly string[] | undefined; now: string; back: ReactNode;
  onSelectionChange?: (id: string | null) => void; download: (filename: string, content: string) => void;
}) {
  const { t, loc } = useReportText();
  const hidden = (s: Section) => state.def.hiddenSections.includes(s);
  const open = (id: string | null) => { store.select(id); onSelectionChange?.(id); };
  const selected = state.selectedId ? report.findings.find((f) => f.id === state.selectedId) : undefined;
  const exportCsv = () => download(`advanced-report-${report.jobId}-${loc}.csv`, toCsv(visibleFindings(report, state.def), state.def.columns, loc, t));
  const notice = state.notice;
  const noticeParams = notice?.params?.status ? { ...notice.params, status: t(`audit-remediation.status.${notice.params.status}`) } : notice?.params;

  return (
    <Frame actions={<>{back}<HeaderSlot jobId={report.jobId} /></>} sub={report.subject.domain ?? report.subject.businessName}>
      <div className="qm-ar-root" data-print={state.printMode ? 'true' : undefined}>
        <ViewsBarSlot state={state} store={store} roles={roles} />
        <div aria-live="polite" className="qm-ar-notice">
          {notice ? <p role={notice.tone === 'error' ? 'alert' : 'status'} data-tone={notice.tone}>{t(notice.key, noticeParams)}</p> : null}
        </div>
        {!hidden('summary') ? <SummarySection report={report} onOpen={(id) => open(id)} /> : null}
        {!hidden('signals') ? <SignalsSection report={report} /> : null}
        {!hidden('findings') ? <FindingsSection report={report} state={state} store={store} onExport={exportCsv} /> : null}
        {!hidden('roadmap') ? <RoadmapSection report={report} now={now} onOpen={(id) => open(id)} /> : null}
        {!hidden('competitors') ? <CompetitorsSection report={report} /> : null}
        {!hidden('coverage') ? <CoverageSection report={report} /> : null}
      </div>
      {selected ? <FindingDrawer report={report} finding={selected} state={state} store={store} roles={roles} onClose={() => open(null)} /> : null}
    </Frame>
  );
}

function ViewsBarSlot({ state, store, roles }: { state: ReportUiState; store: ReportStore; roles: readonly string[] | undefined }) {
  return <ViewsBar state={state} store={store} canEdit={canWriteFindings(roles)} onPrint={() => store.setPrintMode(true)} />;
}

/** Browser download of a text file (no network). */
export function saveFile(filename: string, content: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.rel = 'noopener';
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function printPage(): void { if (typeof window !== 'undefined') window.print(); }
